import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { SvgIconComponent } from "@mui/icons-material";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import PersonSearchRounded from "@mui/icons-material/PersonSearchRounded";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import { MenuIcon } from "../icons";
import AdminPages, { resolveActiveMenuKey } from "../../pages/admin";
import { history } from "../../lib";
import {
  authSelectors,
  Page,
  uiActions,
  uiSelectors,
  useDispatch,
  useSelector,
} from "../../state";

/**
 * Mobile bottom navigation for the admin/agent portal.
 *
 * IA notes (derived from `AdminPages` `menuItemOrder` + `allowedRoles`):
 *  - Dashboard, Applications, and Leads are the three highest-frequency
 *    screens accessible to all customer-facing roles.
 *  - Service Requests is high-frequency but admin-gated, so it appears as
 *    a 4th tab only for roles allowed to view it; otherwise we fall back
 *    to a 4-tab layout.
 *  - "More" reuses the existing left sidebar drawer by toggling
 *    `uiActions.toggleAdminMenuOpen` — same action the header hamburger
 *    triggers — so there is one source of truth for overflow navigation.
 *  - Hidden entirely for `referral_product_owner` (matches sidebar gating
 *    in `AdminLayout`).
 */

const NAV_TAB_KEYS = [
  "dashboard",
  "applications",
  "leads",
  "serviceRequests",
] as const;

type NavTabKey = (typeof NAV_TAB_KEYS)[number];

// Rounded (solid) variants of each tab's icon, used for the active state.
// The outlined variant lives on the shared `AdminPages[key].icon` and is
// used when the tab is inactive, matching common mobile-tab UX (outline
// idle / fill selected).
const ACTIVE_ICONS: Record<NavTabKey | "more", SvgIconComponent> = {
  dashboard: DashboardRounded,
  applications: DescriptionRounded,
  leads: PersonSearchRounded,
  serviceRequests: SupportAgentRounded,
  more: MenuRounded,
};

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1100,
    borderTop: "1px solid #DADFF6",
    borderRadius: "16px 16px 0 0",
    overflow: "hidden",
    paddingBottom: "env(safe-area-inset-bottom)",
    backgroundColor: "white",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  nav: {
    height: 64,
    "& .MuiBottomNavigationAction-root": {
      minWidth: 0,
      padding: "6px 8px",
      color: "#8D98C2",
    },
    "& .MuiBottomNavigationAction-root.Mui-selected": {
      color: theme.palette.primary.main,
    },
    "& .MuiBottomNavigationAction-label": {
      fontSize: 11,
    },
    "& .MuiBottomNavigationAction-label.Mui-selected": {
      fontSize: 12,
    },
  },
}));

const adminPage = (key: string): Page | undefined =>
  (AdminPages as unknown as Record<string, Page>)[key];

const isAllowedForRole = (
  key: string,
  role: string | null | undefined,
): boolean => {
  const page = adminPage(key);
  if (!page) {
    return false;
  }
  if (role === "super_admin") {
    return true;
  }
  if (!page.allowedRoles) {
    return true;
  }
  if (!role) {
    return false;
  }
  return page.allowedRoles.includes(role);
};

export const AdminBottomNav: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const activeRole = useSelector(authSelectors.activeRole);
  const adminMenuOpen = useSelector(uiSelectors.adminMenuOpen);

  // Match sidebar visibility rules from `AdminLayout`.
  const isProductOwner = activeRole === "referral_product_owner";

  const visibleTabs = useMemo<NavTabKey[]>(() => {
    return NAV_TAB_KEYS.filter((key) => isAllowedForRole(key, activeRole));
  }, [activeRole]);

  if (isProductOwner || visibleTabs.length === 0) {
    return null;
  }

  // Resolve via the shared helper first — this honors `parentKey` on
  // detail pages (e.g. `/admin/quote-management/:id` -> Applications).
  const resolvedKey = resolveActiveMenuKey(location.pathname);
  const activeTabKey =
    resolvedKey && (visibleTabs as readonly string[]).includes(resolvedKey)
      ? (resolvedKey as NavTabKey)
      : (visibleTabs
          .map((key) => ({ key, path: adminPage(key)?.path ?? "" }))
          .filter(({ path }) => path && location.pathname.startsWith(path))
          .sort((a, b) => b.path.length - a.path.length)[0]?.key as
          | NavTabKey
          | undefined);

  // The "more" pseudo-value is only used while the drawer is open so the
  // tab feels selected; it is not a real route.
  const value = adminMenuOpen ? "more" : activeTabKey ?? false;

  const handleTabChange = (_: unknown, newValue: string) => {
    if (newValue === "more") {
      dispatch(uiActions.toggleAdminMenuOpen());
      return;
    }
    if (adminMenuOpen) {
      dispatch(uiActions.setAdminMenuOpen(false));
    }
    const target = adminPage(newValue)?.path;
    if (target) {
      history.push(target);
    }
  };

  return (
    <Paper className={classes.paper} elevation={8}>
      <BottomNavigation
        showLabels
        value={value}
        onChange={handleTabChange}
        className={classes.nav}
      >
        {visibleTabs.map((key) => {
          const page = adminPage(key);
          if (!page) {
            return null;
          }
          const isActive = value === key;
          const Icon = isActive ? ACTIVE_ICONS[key] : page.icon;
          return (
            <BottomNavigationAction
              key={key}
              value={key}
              label={page.title}
              icon={Icon ? <Icon /> : undefined}
            />
          );
        })}
        <BottomNavigationAction
          value="more"
          label="More"
          icon={value === "more" ? <ACTIVE_ICONS.more /> : <MenuIcon />}
        />
      </BottomNavigation>
    </Paper>
  );
};

export default AdminBottomNav;
